import { useCallback, useEffect, useState } from 'react';

import {
  KAPCSOLAT_CELOK,
  KAPCSOLAT_CEL_NEVEK,
  KAPCSOLAT_CEL_ROVID,
  api,
  type KapcsolatCel,
} from '../api.ts';
import { useMuvelet } from '../felhasznalo-context.ts';
import { Mezo } from './Mezo.tsx';

/**
 * Egy partner telefonszámai és e-mail címei, helyben szerkeszthetően.
 * (3.0 / 1., 2026-09-27)
 *
 * Azért önálló doboz, mert a sofőrnek **ott kell lennie, ahol dolgozik**:
 * a saját járatlapján és a szállítólevél űrlapján. Neki nincs
 * partner-menüpontja, de ha a helyszínen megkapja a telefonszámot, be
 * tudja írni — a partner többi adatához (ár, cím, számlázás) viszont nem
 * nyúlhat. Ezt az „Elérhetőség rögzítése" engedély szabályozza, a tiltás
 * pedig a szerveren van, nem itt.
 */

interface Kapcsolat {
  id: number;
  ertek: string;
  /** Csak az e-mail címeknél van: mire jó a cím. (3.0 / 5.) */
  cel?: KapcsolatCel;
}

interface Reszletek {
  telefonok: Kapcsolat[];
  emailek: Kapcsolat[];
}

export function ElerhetosegDoboz({
  etterem_id,
  onEmailek,
  csakUj = false,
  cel = 'egyeb',
}: {
  etterem_id: number;
  /** Hány e-mail cím van — a szállítólevél űrlapja ebből tudja, megy-e levél. */
  onEmailek?: (db: number) => void;
  /** Csak a hiányzót lehet beírni; a meglévőt nem írja felül. (2026-09-29) */
  csakUj?: boolean;
  /**
   * Mire kell itt a cím. Amit ezen a képernyőn vesznek fel, ezt a
   * kategóriát kapja — a szállítólevélnél a szállítós címet. (2026-09-30)
   */
  cel?: KapcsolatCel;
}) {
  const rogzithet = useMuvelet('partner_elerhetoseg');
  const [adat, setAdat] = useState<Reszletek | null>(null);
  const [uj, setUj] = useState({ telefon: '', email: '' });
  /* Az iroda maga választ kategóriát az új címhez; a sofőrnél a `cel` dönt. */
  const [ujCel, setUjCel] = useState<KapcsolatCel>(cel);
  const [hiba, setHiba] = useState<string | null>(null);

  const betolt = useCallback(async () => {
    try {
      const v = await api.get<Reszletek>(`/api/etterem/${etterem_id}`);
      setAdat(v);
      onEmailek?.(v.emailek.length);
    } catch {
      /* ha nem látja a partnert, a doboz egyszerűen üresen marad */
    }
  }, [etterem_id]);

  useEffect(() => {
    void betolt();
  }, [betolt]);

  async function muvelet(mit: () => Promise<Reszletek>) {
    try {
      const uj = await mit();
      setAdat(uj);
      onEmailek?.(uj.emailek.length);
      setHiba(null);
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'Az elérhetőséget nem sikerült menteni.');
      await betolt();
    }
  }

  if (!adat) return null;

  const lista = (cim: string, tipus: 'telefon' | 'email', tetelek: Kapcsolat[]) => {
    /*
     * Csak-új módban a meglévő értékhez nem nyúlunk: ha a partnernek már
     * van címe, a sofőr azt a napi munkában nem írhatja át. Ami hiányzik,
     * azt beírhatja, és megmarad. (A megrendelő döntése, 2026-09-29.)
     */
    const szerkesztheto = rogzithet && !csakUj;
    /*
     * Csak-új módban akkor kérhető új cím, ha **ehhez a célhoz** még nincs.
     * Így a sofőr megadhatja a szállítóleveles címet akkor is, ha a partneren
     * már van egy könyvelési cím — de meglévőt továbbra sem ír át.
     */
    const vanMarIlyen =
      tipus === 'email' ? tetelek.some((t) => (t.cel ?? 'egyeb') === cel) : tetelek.length > 0;
    const kerheto = rogzithet && (!csakUj || !vanMarIlyen);
    return (
    <div>
      <label>{cim}</label>
      {tetelek.length === 0 && !rogzithet && <div className="hint">nincs megadva</div>}
      {tetelek.map((t) => (
        <div key={t.id} className="elerhetosor">
          <Mezo
            ertek={t.ertek}
            tiltva={!szerkesztheto}
            onMent={(ertek) =>
              muvelet(() => api.patch<Reszletek>(`/api/etterem/kapcsolat/${t.id}`, { ertek }))
            }
          />
          {tipus === 'email' &&
            (szerkesztheto ? (
              <select
                className="celvalaszto"
                value={t.cel ?? 'egyeb'}
                title="Mire jó ez a cím"
                onChange={(ev) =>
                  void muvelet(() =>
                    api.patch<Reszletek>(`/api/etterem/kapcsolat/${t.id}`, {
                      cel: ev.target.value,
                    }),
                  )
                }
              >
                {KAPCSOLAT_CELOK.map((c) => (
                  <option key={c} value={c}>
                    {KAPCSOLAT_CEL_NEVEK[c]}
                  </option>
                ))}
              </select>
            ) : (
              <span className={`celjel${(t.cel ?? 'egyeb') === cel ? ' ez' : ''}`}>
                {KAPCSOLAT_CEL_ROVID[t.cel ?? 'egyeb']}
              </span>
            ))}
          {szerkesztheto && (
            <button
              className="xb"
              title="Törlés"
              onClick={() => void muvelet(() => api.torol<Reszletek>(`/api/etterem/kapcsolat/${t.id}`))}
            >
              ×
            </button>
          )}
        </div>
      ))}
      {kerheto && (
        <div className="elerhetosor">
          <input
            value={uj[tipus]}
            placeholder={
              csakUj && tipus === 'email' ? `+ ${KAPCSOLAT_CEL_ROVID[cel]} cím…` : '+ új…'
            }
            inputMode={tipus === 'telefon' ? 'tel' : 'email'}
            onChange={(e) => setUj({ ...uj, [tipus]: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && uj[tipus].trim()) void felvesz(tipus);
            }}
            /* Kilépéskor is mentünk: a képernyő többi mezője így viselkedik,
               és aki nem vette észre a + gombot, annak elveszett a beírt
               cím. (A megrendelő jelezte, 2026-09-28.) */
            onBlur={() => {
              if (uj[tipus].trim()) void felvesz(tipus);
            }}
          />
          {tipus === 'email' && szerkesztheto && (
            <select
              className="celvalaszto"
              value={ujCel}
              title="Mire jó ez a cím"
              onChange={(ev) => setUjCel(ev.target.value as KapcsolatCel)}
            >
              {KAPCSOLAT_CELOK.map((c) => (
                <option key={c} value={c}>
                  {KAPCSOLAT_CEL_NEVEK[c]}
                </option>
              ))}
            </select>
          )}
          <button className="xb" title="Felvétel" disabled={!uj[tipus].trim()} onClick={() => void felvesz(tipus)}>
            +
          </button>
        </div>
      )}
      {csakUj && tetelek.length > 0 && rogzithet && (
        <div className="hint">
          rögzítve — javítani az irodában lehet
          {tipus === 'email' && !vanMarIlyen && '; újat az alsó sorban adhatsz meg'}
        </div>
      )}
    </div>
    );
  };

  async function felvesz(tipus: 'telefon' | 'email') {
    const ertek = uj[tipus].trim();
    if (!ertek) return;
    await muvelet(() =>
      api.post<Reszletek>(`/api/etterem/${etterem_id}/kapcsolat`, {
        tipus,
        ertek,
        /* A telefonnál nincs kategória, ott a szerver „egyéb"-re teszi. */
        ...(tipus === 'email' ? { cel: csakUj ? cel : ujCel } : {}),
      }),
    );
    setUj((e) => ({ ...e, [tipus]: '' }));
    if (tipus === 'email' && !csakUj) setUjCel(cel);
  }

  return (
    <div className="elerhetodoboz">
      {hiba && (
        <div className="uzenet bad" onClick={() => setHiba(null)}>
          {hiba}
        </div>
      )}
      <div className="elerhetosegek">
        {lista('Telefon', 'telefon', adat.telefonok)}
        {lista('E-mail', 'email', adat.emailek)}
      </div>
    </div>
  );
}
